import qs from 'qs'
import React, { useContext, useState, useEffect } from 'react'
import { useWindowSize } from 'react-use'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import 'react-dates/initialize'
import { AccountContext } from '@/contexts/account'
import {
  mainContainerHeaderStyle,
  headerBarLeftWrapperStyle,
  tableHeaderThStyle,
  tableBodyTdStyleRead,
  tableBodyTdStyleUnread,
  tableBodyTdStyle,
} from '@/constants/layout'
import { updateSelfCheckin, fetchAdminReservationForCheckin, insertAdminReservationCheckin, updateSalesAdvancePayment } from '@/apis/aipass'
import { Button, ButtonType } from '@/components/atoms/button'
import { ReloadButton } from '@/components/atoms/reload-button'
import { AccommodationPaging } from '@/components/molecules/accommodation-paging'
import { reloadWrapperStyle, guestNameUnreadEnStyle, guestNameUnreadJaStyle } from '@/components/pages/accommodation-management/list-layout'
import { DetailedReserchPanel } from '@/components/pages/accommodation-management/_reservation/detailed-reserch-panel'
import { useSearchedConditions } from '@/hooks/use-search-condition'
import { dividedRoomNumber, dividedTypeName } from '@/libs/divide-list'
import { ApprovedStatusType } from '@/models/reservation-approved-status'
import { ReservationCreateModal } from '@/components/organisms/accommodation-management/reservation-create-modal'
import { ReservationCreateWithRoomModal } from '@/components/organisms/accommodation-management/reservation-create-with-room-modal'
import { setHasRoomManagerPlugin } from '@/libs/plugins'
import { convertLabelWithEntireSearch, EntireSearchContentType } from '@/models/entire-search'
import { SortableTableHeader, SortKeyType } from '@/components/molecules/sortable-table-header'
import { SortKey } from '../list'
import { SearchReservationList } from '@/models/reservation'

type ContainerProps = {
  reservationList: SearchReservationList[]
  page: number
  setPage: (v: number) => void
  setIsLoading: (isLoading: boolean) => void
  hasList: number
  listTotal: number
  limit: number
  pagingIsDisabled: boolean
  isStatusApproved
  setIsStatusApproved
  onClickReserch: () => void
  onClickClearReserch: () => void
  detailedReserch: EntireSearchContentType
  setDetailedReserch: any
  sortKey: SortKeyType
  onChangeSortKey: (sort: SortKeyType) => void
  listScrollRef?: React.RefObject<HTMLDivElement>
}

export const ReservationList: React.FC<ContainerProps> = ({
  reservationList,
  page,
  setPage,
  setIsLoading,
  hasList,
  listTotal,
  limit,
  pagingIsDisabled,
  isStatusApproved,
  setIsStatusApproved,
  onClickReserch,
  onClickClearReserch,
  detailedReserch,
  setDetailedReserch,
  ...props
}) => {
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const { plugins } = useContext<any>(AccountContext)
  const windows = useWindowSize()
  const hasRoomManagerPlugin = setHasRoomManagerPlugin(plugins)
  const [isReservationCreateModalOpen, setIsReservationCreateModalOpen] = useState<boolean>(false)
  const [isDropDownReserchPanelOpen, setIsDropDownReserchPanelOpen] = useState<boolean>(false)
  const [checkedStatusApproved] = useState<boolean>(false)
  const [checkedReservationIds, setCheckedReservationIds] = useState<object>({})
  const [checkedAllFlag, setCheckedAllFlag] = useState<boolean>(false)
  const [checkedTotalCount, setCheckedTotalCount] = useState<number>(0)
  const { SearchedConditionValues } = useSearchedConditions(convertLabelWithEntireSearch('reservation'))

  // Checkbox when status update (single) is selected
  const handleCheckboxChange = (e, index) => {
    // Empty if already checked
    let newValue = ''
    if (e.target.value !== checkedReservationIds[index]) {
      newValue = e.target.value
      const newIds = { ...checkedReservationIds, [index]: newValue }
      setCheckedReservationIds(newIds)
    }

    if (e.target.value === checkedReservationIds[index]) {
      delete checkedReservationIds[index]
      const newIds = { ...checkedReservationIds, [index]: newValue }
      setCheckedReservationIds(newIds)
    }
    // EX) When checking 0 and 2 (when checking 1 and unchecking) {0: '123', 1: '', 2: '234'}
  }

  // Checkbox when status update (whole) is selected
  const handleAllCheckboxChange = index => {
    if (!checkedAllFlag) {
      const allIds = reservationList.map(v => v.reservationId)
      setCheckedAllFlag(true)
      setCheckedReservationIds(allIds)
    } else {
      setCheckedAllFlag(false)
      setCheckedReservationIds([])
    }
  }

  // Push booking id and approvedStatus on status update
  const onSubmit = async () => {
    try {
      setIsLoading(true)
      // Format the array, excluding if it is empty
      const formattedReservationIds: string[] = []

      Object.keys(checkedReservationIds).forEach(function (key) {
        if (checkedReservationIds[key] !== '') {
          formattedReservationIds.push(checkedReservationIds[key])
        }
      })

      const res = await fetchAdminReservationForCheckin('', '', '', page, 0, formattedReservationIds)

      // Initialize when acquiring for the first time
      for (const reservation of res?.reservations || []) {
        await reservationCheckinLink(reservation)
      }

      // Give Advance Receipt Flag
      await updateSalesAdvancePayment(formattedReservationIds)
    } catch (error) {
      console.warn(error)
    } finally {
      onClickReserch()
    }
  }

  // Batch creation of reservation list
  const reservationCheckinLink = async reservation => {
    const selectedReservation = reservation

    const userCheckinTime = selectedReservation.checkinDate ? dayjs(selectedReservation.checkinDate).unix() : null
    const checkoutTime = selectedReservation.checkoutDate ? dayjs(selectedReservation.checkoutDate).unix() : null

    const checkinVariables = {
      hotelId: selectedReservation.hotelId,
      approvedStatus: ApprovedStatusType.Checkin,
      userCheckinTime,
      checkoutTime,
      guest: {
        // Show booker info if guest name is empty
        name: _convertNullToEmpty(selectedReservation.guestName) || _convertNullToEmpty(selectedReservation.name),
        nameKana: _convertNullToEmpty(selectedReservation.guestNameKana) || _convertNullToEmpty(selectedReservation.nameKana),
        birthDate: null,
        nationality: null,
        gender: null,
        postalCode: null,
        address: selectedReservation.guestName ? selectedReservation.guestAddr : selectedReservation.addr,
        telephone: selectedReservation.guestName ? selectedReservation.guestTel || '-' : selectedReservation.tel || '-',
        email: selectedReservation.guestName
          ? _convertEmptyToNull(selectedReservation.guestMailAddr)
          : _convertEmptyToNull(selectedReservation.mailAddr),
        occupation: null,
        otherOccupation: null,
        totalMember: selectedReservation.paxTotal,
        picture: null,
        credential: null,
        company: null,
      },
      accompany: [],
      previousPlaceToStay: '自宅',
      nextPlaceToStay: '自宅',
      reservationId: selectedReservation.reservationId,
    }

    // Create check-in information
    await updateSelfCheckin(checkinVariables, selectedReservation.reservationId).then(async res => {
      // Link check-in and booking
      await insertAdminReservationCheckin({
        checkinId: res.checkinId,
        reservationIds: [selectedReservation.reservationId],
      })
    })
  }

  const _convertEmptyToNull = value => {
    return value ? value : null
  }

  const _convertNullToEmpty = value => {
    return value ? value : ''
  }

  // Number of status change checks
  useEffect(() => {
    let count = 0
    Object.keys(checkedReservationIds).forEach(key => {
      if (checkedReservationIds[key] !== '') {
        count++
      }
    })
    setCheckedTotalCount(count)
    if (count === 0) {
      setCheckedAllFlag(false)
    }
    if (count > 0 && count === hasList) {
      setCheckedAllFlag(true)
    } else {
      setCheckedAllFlag(false)
    }
  }, [checkedReservationIds])

  return (
    <>
      <div css={[mainContainerHeaderStyle, { minWidth: 'auto' }]}>
        <div css={headerBarLeftWrapperStyle}>
          {!isStatusApproved && (
            <>
              <div>
                <Button
                  buttonType={4}
                  width={i18n.language === 'en' ? 118 : 90}
                  height={32}
                  fontSize={12}
                  onClick={() => setIsDropDownReserchPanelOpen(!isDropDownReserchPanelOpen)}
                >
                  {t('Detailed search')}
                </Button>
                {isDropDownReserchPanelOpen && (
                  <DetailedReserchPanel
                    onClickReserch={onClickReserch}
                    onClickClearReserch={onClickClearReserch}
                    setIsDropDownReserchPanelOpen={() => setIsDropDownReserchPanelOpen(!isDropDownReserchPanelOpen)}
                    detailedReserch={detailedReserch}
                    setDetailedReserch={setDetailedReserch}
                  />
                )}
              </div>
              <div css={reloadWrapperStyle}>
                <ReloadButton onClick={onClickReserch} />
              </div>

              <SearchedConditionValues />
            </>
          )}
          {isStatusApproved && (
            <>
              <div css={selectedNumberStyle}>
                {checkedTotalCount}
                {t('item selected')}
              </div>
              <Button
                buttonType={checkedTotalCount ? ButtonType.Secondary : ButtonType.BorderDisabled}
                isDisabled={!checkedTotalCount}
                width={152}
                height={32}
                fontSize={12}
                marginLeft={24}
                onClick={() => {
                  onSubmit()
                  setIsStatusApproved(false)
                  setCheckedReservationIds({})
                  setCheckedAllFlag(false)
                }}
              >
                {t('Change to checkin')}
              </Button>
              <Button
                buttonType={4}
                width={102}
                height={32}
                fontSize={12}
                marginLeft={8}
                onClick={() => {
                  setIsStatusApproved(false)
                  setCheckedReservationIds({})
                  setCheckedAllFlag(false)
                }}
              >
                {t('Cancel')}
              </Button>
            </>
          )}
        </div>
        <div>
          <div css={ReservationHeaderButtonStyle}>
            <div css={dropDownButtonStyle}>
              <Button
                buttonType={1}
                width={i18n.language === 'en' ? 176 : 124}
                height={32}
                fontSize={12}
                icon={require('@/static/images/new_reservation.svg')}
                onClick={() => setIsReservationCreateModalOpen(true)}
              >
                {t('New reservation')}
              </Button>
            </div>
            <AccommodationPaging page={page} setPage={setPage} total={listTotal} limit={limit} isDisabled={pagingIsDisabled} />
            {isReservationCreateModalOpen &&
              (hasRoomManagerPlugin ? (
                <ReservationCreateWithRoomModal
                  setIsModalOpen={setIsReservationCreateModalOpen}
                  onCreated={({ reservationUuid, reservationId }) => {
                    history.push({
                      pathname: `/reservation/${reservationUuid}`,
                      search: qs.stringify({ reservationId }),
                    })
                  }}
                />
              ) : (
                <ReservationCreateModal setIsModalOpen={setIsReservationCreateModalOpen} />
              ))}
          </div>
        </div>
      </div>
      <div ref={props.listScrollRef} css={{ overflow: 'auto', height: windows.height - 218 }}>
        <table css={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {isStatusApproved && (
                <th css={[tableHeaderThStyle, firstTdStyle]}>
                  <input
                    id="checkboxAll"
                    alt="checkbox"
                    type="image"
                    src={require(`@/static/images/check-box${checkedAllFlag === true ? '' : '_off'}.svg`)}
                    onClick={index => handleAllCheckboxChange(index)}
                  />
                </th>
              )}

              <SortableTableHeader
                name={SortKey.ReserveUserName}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Reservation person')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.ReserveGuestName}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Guest')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.ReservationId}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Reservation ID')}
              </SortableTableHeader>
              <SortableTableHeader name={SortKey.RoomType} onChange={props.onChangeSortKey} value={props.sortKey} css={tableHeaderThStyle}>
                {t('Room type')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.RoomNumber}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Room number')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.ReserveStatus}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Status')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.CheckinDate}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Date of stay')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.ArrivalTime}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Arrival times')}
              </SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {reservationList?.map((v, index) => {
              return (
                <Link
                  key={index}
                  css={[
                    v.reservationRead ? tableBodyTdStyleRead : tableBodyTdStyleUnread,
                    { display: 'table-row', ':hover': { color: '#000' } },
                  ]}
                  to={{
                    pathname: `/reservation/${v.id}`,
                    search: qs.stringify({ reservationId: v.reservationId }),
                  }}
                >
                  {isStatusApproved && (
                    <td css={[tableBodyTdStyle, firstTdStyle]} onClick={e => e.stopPropagation()}>
                      <input
                        id="checkinId"
                        alt="checkboxList"
                        value={v.reservationId}
                        type="image"
                        src={require(
                          `@/static/images/check-box${
                            checkedReservationIds[index] === v.reservationId || checkedStatusApproved ? '' : '_off'
                          }.svg`,
                        )}
                        onClick={e => {
                          e.preventDefault()
                          handleCheckboxChange(e, index)
                        }}
                      />
                    </td>
                  )}
                  <td css={[tableBodyTdStyle, !isStatusApproved ? { paddingLeft: 32 } : {}]}>
                    <div css={i18n.language === 'en' ? guestNameUnreadEnStyle : guestNameUnreadJaStyle}>
                      <div css={SubscriberNameKanaStyle}>{v.nameKana ? v.nameKana : '-'}</div>
                      <div css={SubscriberNameStyle}>{v.name ? v.name : '-'}</div>
                    </div>
                  </td>
                  <td css={tableBodyTdStyle}>
                    <div css={i18n.language === 'en' ? guestNameUnreadEnStyle : guestNameUnreadJaStyle}>
                      <div css={SubscriberNameKanaStyle}>{v.guestNameKana ? v.guestNameKana : '-'}</div>
                      <div css={SubscriberNameStyle}>{v.guestName ? v.guestName : '-'}</div>
                    </div>
                  </td>
                  <td css={reservationIdTdStyle}>
                    <div css={!v.reservationRead ? unreadStyle : readStyle}>
                      <div css={reservationIdStyle}>{v.reservationId ? v.reservationId : '-'}</div>
                    </div>
                  </td>
                  <td css={[tableBodyTdStyle, { maxWidth: 170 }]}>
                    <div css={[roomTypeStyle, !v.reservationRead ? unreadDataRoomNumberStyle : readDataRoomNumberStyle]}>
                      {dividedTypeName(v.rooms)}
                    </div>
                  </td>
                  <td css={[tableBodyTdStyle, { maxWidth: 150 }]}>
                    <div css={roomNumberStyle}>
                      {v.rooms.length !== 0 ? (
                        <div css={!v.reservationRead ? unreadDataRoomNumberStyle : readDataRoomNumberStyle}>
                          {dividedRoomNumber(v.rooms)}
                        </div>
                      ) : (
                        '-'
                      )}
                    </div>
                  </td>
                  <td css={[tableBodyTdStyle]}>
                    <div css={statusTagStyle}>
                      {v.reservationStatus === 1 && <div css={statusTagOrangeStyle}>{t('New')}</div>}
                      {v.reservationStatus !== 1 && (
                        <div css={statusTagGrayStatus}>
                          {v.reservationStatus === 2 && t('Update')}
                          {v.reservationStatus === 3 && t('Cancel')}
                          {v.reservationStatus === 4 && t('No show')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td css={[tableBodyTdStyle, { minWidth: 118 }]}>
                    <div css={[reservationDateStyle, !v.reservationRead ? unreadStyle : readStyle]}>
                      {v.checkinDate === null ? (
                        '-'
                      ) : (
                        <>
                          <span css={reservationDateStyle}>{dayjs(v.checkinDate).format(t('YYYY'))}</span>
                          {dayjs(v.checkinDate).format(t('MM-DD'))}
                          <span style={{ paddingLeft: 5 }}>
                            {v.nights ?? 0}
                            {t('Night')}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td css={[tableBodyTdStyle]}>
                    <div css={!v.reservationRead ? unreadStyle : readStyle}>{v.checkinTime === null ? '-' : v.checkinTime}</div>
                  </td>
                </Link>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

const ReservationHeaderButtonStyle = css({
  display: 'flex',
  position: 'relative',
})

const dropDownButtonStyle = css({
  position: 'relative',
})

const dataTextStyle = css({
  paddingRight: 8,
  textAlign: 'left',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const firstTdStyle = css({
  paddingLeft: 32,
  '@media(max-width: 1120px)': { paddingLeft: 16 },
})

const SubscriberNameKanaStyle = css(dataTextStyle, {
  fontSize: 12,
  letterSpacing: 0.48,
  lineHeight: 1.5,
  color: '#676767',
})

const SubscriberNameStyle = css(dataTextStyle, {
  fontSize: 14,
  lineHeight: 1.5,
})

const reservationIdTdStyle = css(tableBodyTdStyle, {
  maxWidth: 180,
  '@media(max-width: 1080px)': {
    maxWidth: 140,
  },
})
const reservationIdStyle = css(dataTextStyle, {
  fontSize: 14,
  lineHeight: 2.5,
  color: '#272727',
})

const roomTypeStyle = css(dataTextStyle, {
  fontSize: 14,
  lineHeight: 2.5,
  color: '#272727',
})

const roomNumberStyle = css(dataTextStyle, {
  fontSize: 14,
  lineHeight: 2.5,
  color: '#272727',
  minWidth: 100,
})

const statusTagStyle = css({
  textAlign: 'center',
  fontSize: 12,
})

const statusTagGrayStatus = css({
  height: 22,
  width: 88,
  border: '1px solid #676767',
  borderRadius: 5,
  color: '#676767',
  padding: '4px 10px',
})

const statusTagOrangeStyle = css({
  height: 22,
  minWidth: 78,
  width: 88,
  border: '1px solid #F2A40B',
  borderRadius: 5,
  color: '#F2A40B',
  padding: '4px 10px',
})

const unreadStyle = css({
  fontWeight: 'bold',
})

const readStyle = css({
  fontWeight: 'normal',
})

const reservationDateStyle = css({
  '@media(max-width: 1120px)': {
    display: 'block',
  },
})

const unreadDataRoomNumberStyle = css(unreadStyle, dataTextStyle)

const readDataRoomNumberStyle = css(readStyle, dataTextStyle)

const dropDownIcon = css({
  marginLeft: 8,
})

const selectedNumberStyle = css({
  fontSize: 14,
  fontWeight: 'bold',
  margin: 'auto',
})
